import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Slate, Editable, withReact, ReactEditor, useSlate} from 'slate-react';
import styled from 'styled-components'
import {createEditor, Transforms, Node, Editor} from "slate";
import {withHistory} from "slate-history";
import {useTranslation} from "react-i18next";
import {RowPopOverList} from "./_rowPopOverList";
import {Text} from "../../common/text";
import {THEME_SEMANTICS} from "../../common/theme/_semantics";
import {THEME_COLORS} from "../../common/theme/_colors";

const serialize = value => {
    return (
        value
            // Return the string content of each paragraph in the value's children.
            .map(n => Node.string(n))
            // Join them all with line breaks denoting paragraphs.
            .join('\n')
    )
}
const deserialize = string => {
    // Return a value array of children derived by splitting the string.
    return string.split('\n').map(line => {
        return {
            children: [{ text: line }],
        }
    })
}
const SlateEditor = ({initialValue, message, slateValue, functionProps, maxLength= 255, ...props}) => {
    const MAX_LENGTH = maxLength
    const {t} = useTranslation()

    const editor = useMemo(
        () => withInlines(withReact(withHistory(createEditor()))),
        []
    )
    // const [value, setValue] = useState(deserialize(initialValue));
    const [value, setValue] = useState(message);
    const [targetElement, setTargetElement] = useState(null)
    const [addTag, setAddTag] = useState(null)
    const [changeTag, setChangeTag] = useState(null)
    const [focus, setFocus] = useState(false)
    const renderElement = useCallback((props) => <CustomElement
        clickPopover={handleClickPopover} {...props} />, []);
    const { insertText, insertData } = editor;
    const handleClickPopover = (e, target) => {
        setTargetElement(target)
        setChangeTag(e.currentTarget)
    }
    const handleClickClose = _=>{
        setAddTag(null)
        setChangeTag(null)
    }

    const handleChangeTagName = val => {
        if (targetElement) {
            const path = ReactEditor.findPath(editor, targetElement);
            const newElement = {
                type: 'tag',
                children: [{text: `{${val}}`}],
            }
            const newPath = [path[0], path[1] + 2]
            Transforms.insertNodes(editor, newElement, {at: path});
            Transforms.removeNodes(editor, {at: newPath})
            setTargetElement(null);
            setChangeTag(null)
            functionProps?.onChange(val)
        }
    }
    const onClickFocus = () => {
        setFocus(true)
    }

    const handleChange = val => {
        const revertText = serialize(val)
        if (functionProps?.onChange)
            functionProps?.onChange(val)
        setValue(val);
    }
    const insertCustomSpan = (val) => {
        const mention = {
            type: 'tag',
            children: [{ text: `{${val}}` }],
        };

        // Save scroll position to restore later
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        const { selection } = editor;
        if (!selection) return;

        // Insert the node
        Transforms.insertNodes(editor, mention);

        // Move cursor to after the new node
        const nextPoint = Editor.after(editor, editor.selection.focus, { unit: 'offset' });
        if (nextPoint) {
            Transforms.select(editor, nextPoint);
        }

        // Delay to allow DOM update, then focus and restore scroll
        setTimeout(() => {
            ReactEditor.focus(editor);
            window.scrollTo(scrollX, scrollY); // prevent page from jumping
        }, 0);

        setAddTag(null);
    }
    const handleOnBlur = _=>{
        functionProps.onBlurSlate()
        setFocus(false)
    }
    const handleKeyDown = (event) => {
        if (slateValue.textCount >= MAX_LENGTH && event.key !== 'Backspace' && event.key !== 'Delete') {
            event.preventDefault();
        }
    };


    const handleReset = useCallback((value) => {
        Transforms.deselect(editor);
        editor.children = value;
        Transforms.select(editor, { path: [0, 0], offset: 0 });
        ReactEditor.focus(editor);
        editor.onChange();
    }, [editor]);
    useEffect(()=>{
        if(!!slateValue?.resetDefault)
            handleReset(initialValue)
    },[slateValue?.resetDefault])
    return (
        <StyleEditor>
            <div
                className={'slate-js-layout'}
                onClick={onClickFocus}
                data-error={!!slateValue?.validate}
                data-focus={!!focus && slateValue?.validate === ''}
            >
                <Slate
                    key={JSON.stringify(message)}
                    editor={editor}
                    initialValue={initialValue}
                    value={value}
                    onChange={(newValue) => handleChange(newValue)}
                >
                    <Editable
                        renderElement={renderElement}
                        style={{minHeight: 114}}
                        className={'slate-js-edit'}
                        placeholder={slateValue?.placeHolder}
                        onBlur={handleOnBlur}
                        onKeyDown={handleKeyDown}
                    />
                </Slate>
                <div className='live-stream-message_notice_add-tag'>
                    <div onClick={(e) => setAddTag(e.currentTarget)}>
                        <div style={{float: 'left', display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer'}}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 4V16M4 10H16" stroke="#1A94FF" strokeWidth="1.4" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                            </svg>
                            <Text fontWeight={600}
                                  color={THEME_SEMANTICS.delivering}>{t('facebookManagement_addTag')}</Text>
                        </div>
                    </div>
                    <Text fontSize={13} color={'#cacbd4'}>
                        {slateValue?.textCount || 0} /{maxLength}
                    </Text>
                </div>
            </div>
            {
                !!slateValue?.validate && <Text fontSize={12} className={'slate-js-validate'} color={THEME_SEMANTICS?.failed}>{t(slateValue?.validate)}</Text>
            }
            {
                !!changeTag && <RowPopOverList
                    list={slateValue?.listPopover}
                    target={changeTag}
                    isAdd={false}
                    onChangeTagName={handleChangeTagName}
                    onClose={handleClickClose}
                />
            }
            {
                !!addTag && <RowPopOverList
                    list={slateValue?.listPopover}
                    target={addTag}
                    isAdd={true}
                    onChangeTagName={insertCustomSpan}
                    onClose={handleClickClose}
                />
            }
        </StyleEditor>

    );
};

export default SlateEditor;

const CustomElement = ({clickPopover, ...props}) => {
    const {attributes, children, element} = props;
    if (element.type === 'tag') {
        return (
            <span
                {...attributes}
                contentEditable={false}
                className={"custom-span"}
                onClick={(e) => clickPopover(e, element)}
            >
                {children}
            </span>
        );
    }
    return <p {...attributes} style={{lineHeight: '30px', fontSize: 14, fontWeight: 400}}>{children}</p>
};
const withInlines = editor => {
    const {isInline, isElementReadOnly, isSelectable} = editor
    editor.isInline = element => element.type === 'tag' || isInline(element);

    editor.isElementReadOnly = element => element.type === 'tag' || isElementReadOnly(element);

    editor.isSelectable = element => element.type !== 'tag' && isSelectable(element);
    return editor
}
const StyleEditor = styled.div`
   margin-bottom: 8px;
   .slate-js-layout{
        border: 1px solid #ebeef5;
        border-radius: 6px;
        &[data-error = true]{
          border: 1px solid ${THEME_SEMANTICS.failed};
        }
        &[data-focus = true]{
          border: 1px solid ${THEME_COLORS.secondary_800};
        }
   } 
  .slate-js-edit{
    min-height: 114px;
    width: 100%;
    padding: 16px;
    span{
      &[data-slate-placeholder = true]{
        top: 16px !important;
        font-weight: 400 !important;
        font-size: 14px !important;
        color: #7c91ba !important;
      }
    }
  }
  .editor {
      border: 1px solid #ddd;
      padding: 10px;
      min-height: 100px;
  }
  .custom-span {
    cursor: pointer;
    width: fit-content;
    padding: 0 8px;
    border-radius: 4px;
    border: 1px solid #1162FF;
    color: #1162FF;
    font-size: 14px;
    font-weight: 600;
  }

`

